import { defineStore } from 'pinia';
import { Dialog, Notify } from 'quasar';
import getDB from 'src/functions/db';
import { showLoadingNotification } from 'src/functions/function-show-notifications';

import { useGlobalStore } from './useGlobalStore';
import { useUserStore } from './useUserStore';

// A waiting service worker never activates on its own: reloading the page is not
// enough, the worker has to be told to skip waiting. Without this message the new
// version stays waiting forever and every load re-announces the same update.
const SKIP_WAITING = 'SKIP_WAITING';
// Last-resort guard so a worker that refuses to take control can't reload us in a loop.
const RELOAD_MARK = 'updateStore.lastReload';
const RELOAD_COOLDOWN = 30000;
// A worker that never claims the page still lets us reload, just later.
const CONTROLLER_CHANGE_TIMEOUT = 5000;
// Long enough to save a sentence or abandon a form, short enough that an
// operator watching the panel sees the version change while they wait.
const FORCED_UPDATE_COUNTDOWN = 5;

export const useUpdateStore = defineStore('Update', {
  state: () => ({
    awaitingRestart: false,
    restartRequired: false,
    installing: false,
    forcing: false
  }),
  actions: {
    async checkForUpdates({ notification = true, forced = false }) {
      const GlobalStore = useGlobalStore();

      if (!('serviceWorker' in navigator) || !GlobalStore.connectionStatus || !GlobalStore.connectionStatus.connected) {
        return;
      }

      if (notification) {
        showLoadingNotification('Vérification des mises à jour...');
      }
      if (forced === true) {
        this.restartRequired = true;
      }

      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(registration => registration.update()));

        if (registrations.some(registration => registration.waiting)) {
          this.awaitingRestart = true;
        }
      } catch (error) {
        console.warn("Can't update service worker:", error);
      }
    },

    async installUpdate() {
      if (this.installing) {
        return;
      }
      this.installing = true;

      try {
        await this.checkForUpdates({ notification: false });

        const registration = 'serviceWorker' in navigator ? await navigator.serviceWorker.getRegistration() : null;
        const waiting = registration ? registration.waiting : null;

        if (waiting) {
          try {
            await new Promise(resolve => {
              let timer = null;
              const done = () => {
                clearTimeout(timer);
                resolve();
              };

              navigator.serviceWorker.addEventListener('controllerchange', done, { once: true });
              timer = setTimeout(done, CONTROLLER_CHANGE_TIMEOUT);
              waiting.postMessage({ type: SKIP_WAITING });
            });
          } catch (error) {
            // Reloading is still worth a try even if the worker refused the message.
            console.warn("Can't hand over to the waiting service worker:", error);
          }
        }

        this.reload();
      } finally {
        this.installing = false;
      }
    },

    /**
     * An administrator decided this client updates now. Reloading somebody's
     * browser without warning eats whatever they were typing, so they get a few
     * seconds and a reason — but not a choice, which is the point of forcing.
     */
    async installUpdateWithCountdown(reason) {
      if (this.forcing) {
        return;
      }
      this.forcing = true;
      this.restartRequired = true;

      let remaining = FORCED_UPDATE_COUNTDOWN;
      const notify = Notify.create({
        group: false,
        timeout: 0,
        color: 'warning',
        textColor: 'black',
        icon: 'system_update',
        message: reason,
        caption: `Redémarrage dans ${remaining} s…`
      });

      await new Promise(resolve => {
        const tick = setInterval(() => {
          remaining -= 1;
          if (remaining <= 0) {
            clearInterval(tick);
            resolve();
            return;
          }
          notify({ caption: `Redémarrage dans ${remaining} s…` });
        }, 1000);
      });

      notify({ caption: 'Redémarrage…' });
      try {
        await this.installUpdate();
      } finally {
        this.forcing = false;
      }
    },

    reload() {
      const lastReload = Number(sessionStorage.getItem(RELOAD_MARK) || 0);

      if (lastReload && Date.now() - lastReload < RELOAD_COOLDOWN) {
        console.warn('Update reload skipped: the page already reloaded for an update moments ago.');
        return;
      }

      sessionStorage.setItem(RELOAD_MARK, String(Date.now()));
      window.location.reload();
    },

    /**
     * Everything the app keeps on this device. Split out of `uninstall` so a
     * remote reset can reach it without going through a confirmation the
     * operator has already given on their side.
     */
    async purge({ data = false } = {}) {
      if ('caches' in window) {
        try {
          const cacheNames = await caches.keys();
          await Promise.all(cacheNames.map(cacheName => caches.delete(cacheName)));
        } catch (error) {
          console.warn("Can't clear caches:", error);
        }
      }

      if ('serviceWorker' in navigator) {
        try {
          const registrations = await navigator.serviceWorker.getRegistrations();
          await Promise.all(registrations.map(registration => registration.unregister()));
        } catch (error) {
          console.warn("Can't unregister service workers:", error);
        }
      }

      if (data) {
        try {
          await getDB().delete();
        } catch (error) {
          console.warn("Can't delete the local database:", error);
        }
      }
    },

    async uninstall(reset = true) {
      await this.purge();

      if (!reset) {
        return;
      }

      Dialog.create({
        title: 'Réinitialisation',
        message: "Tous les paramètres de l'application seront réinitialisés, vous devrez vous reconnecter.",
        cancel: true,
        persistent: true
      }).onOk(() => void this.resetAndSignOut());
    },

    /**
     * The reset itself, with nobody asked. The settings drawer asks first; a
     * remote reset does not, because the operator already did and the device it
     * lands on may have nobody in front of it.
     */
    async resetAndSignOut() {
      const UserStore = useUserStore();

      await this.purge({ data: true });
      UserStore.logout();
    }
  }
});
